import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Download, Upload, Undo2 } from "lucide-react";
import api from "@/lib/api";
import useCrmConfig from "@/hooks/useCrmConfig";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loading } from "@/components/common/Loading";

const STATE_STYLE = {
  new: "bg-emerald-100 text-emerald-800", existing: "bg-sky-100 text-sky-800",
  possible_match: "bg-amber-100 text-amber-900", conflict: "bg-orange-100 text-orange-900",
  skip: "bg-secondary text-foreground/60",
};
const STATES = ["new", "existing", "possible_match", "conflict", "skip"];

export default function ContactImport() {
  const { visible, loading } = useCrmConfig();
  const [kind, setKind] = useState("csv");
  const [text, setText] = useState("");
  const [batch, setBatch] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/crm/import/batches");
      setHistory(data.batches || []);
    } catch { setHistory([]); }
  }, []);

  useEffect(() => { if (visible) loadHistory(); }, [visible, loadHistory]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error("That file is larger than 1 MB"); return; }
    setText(await file.text());
    setKind(file.name.toLowerCase().endsWith(".vcf") ? "vcard" : "csv");
  };

  const preview = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/crm/import/preview", { text, kind });
      setBatch(data);
      setDecisions(Object.fromEntries(data.rows.map((r) => [r.index, r.decision])));
    } catch (e) {
      const code = e?.response?.data?.detail;
      const message = code === "crm_import_too_large" ? "That file is larger than 1 MB."
        : code === "crm_import_no_recognized_columns"
          ? "No recognizable columns were found. A header row with names like First Name, Last Name, Email is required."
          : code === "crm_import_empty" ? "That file looked empty."
            : code === "crm_import_no_cards" ? "No vCards were found in that file."
              : code === "crm_import_kind_invalid" ? "Choose CSV or vCard."
                : "Couldn't read that file.";
      setError(message);
      toast.error(message);
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/crm/import/${batch.id}/commit`, { decisions });
      toast.success(`Imported ${data.created} new, updated ${data.updated}`);
      setBatch(null); setText(""); loadHistory();
    } catch { toast.error("Couldn't complete that import"); } finally { setBusy(false); }
  };

  const undo = async (id) => {
    try {
      const { data } = await api.post(`/crm/import/${id}/undo`);
      toast.success(`Removed ${data.removed} imported contact(s)`);
      loadHistory();
    } catch { toast.error("Couldn't undo that import"); }
  };

  if (loading) return <Loading />;
  if (!visible) return (
    <div className="py-20 text-center" data-testid="import-unavailable">
      <h1 className="text-2xl font-[680]">Page not found</h1>
    </div>
  );

  return (
    <div data-testid="contact-import-page">
      <Link to="/dashboard/contacts" data-testid="import-back"
        className="inline-flex items-center gap-1.5 text-sm font-[560] text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to contacts
      </Link>
      <h1 className="mt-4 text-2xl font-[680] tracking-[-0.01em]">Import &amp; export</h1>
      <p className="mt-1 text-muted-foreground">
        Bring in a CSV or vCard file, review every row, then commit. Imports never overwrite
        anything you typed yourself.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/export/csv`} data-testid="export-csv">
          <Button variant="outline" size="sm">
            <Download className="mr-1.5 h-4 w-4" /> Export CSV
          </Button>
        </a>
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/export/csv?include_notes=true`}
          data-testid="export-csv-notes">
          <Button variant="outline" size="sm">
            <Download className="mr-1.5 h-4 w-4" /> Export with notes
          </Button>
        </a>
      </div>

      {!batch ? (
        <section className="mt-6 rounded-xl border border-border bg-card p-4"
          data-testid="import-form">
          <div className="flex flex-wrap items-center gap-3">
            <input type="file" accept=".csv,.vcf,text/csv,text/vcard" onChange={onFile}
              data-testid="import-file" aria-label="Choose a CSV or vCard file"
              className="text-sm" />
            <div className="flex gap-1.5">
              {["csv", "vcard"].map((k) => (
                <button key={k} type="button" onClick={() => setKind(k)}
                  aria-pressed={kind === k} data-testid={`import-kind-${k}`}
                  className={`rounded-full px-3 py-1 text-sm ${kind === k
                    ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground/70"}`}>
                  {k === "csv" ? "CSV" : "vCard"}
                </button>
              ))}
            </div>
          </div>
          <Textarea rows={7} value={text} onChange={(e) => setText(e.target.value)}
            data-testid="import-text" aria-label="Paste file contents" className="mt-3 font-mono text-xs"
            placeholder="…or paste the file contents here" />
          <Button className="mt-3" onClick={preview} disabled={busy || !text.trim()}
            data-testid="import-preview">
            <Upload className="mr-1.5 h-4 w-4" /> Preview import
          </Button>
          {error && (
            <p className="mt-2 text-sm text-destructive" role="alert" data-testid="import-error">
              {error}
            </p>
          )}
        </section>
      ) : (
        <section className="mt-6" data-testid="import-preview-table">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground" data-testid="import-counts">
              {Object.entries(batch.counts).filter(([, n]) => n > 0)
                .map(([k, n]) => `${n} ${k.replace("_", " ")}`).join(" · ")}
            </p>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setBatch(null)}
                data-testid="import-cancel">Cancel</Button>
              <Button size="sm" onClick={commit} disabled={busy} data-testid="import-commit">
                Commit import
              </Button>
            </div>
          </div>
          {batch.warnings?.length > 0 && (
            <p className="mt-2 text-xs text-amber-700" data-testid="import-warnings">
              {batch.warnings.join(" ")}
            </p>
          )}
          <ul className="mt-3 overflow-hidden rounded-xl border border-border bg-card">
            {batch.rows.map((r) => (
              <li key={r.index} data-testid={`import-row-${r.index}`}
                className="flex flex-col gap-2 border-b border-border px-3 py-2.5 last:border-0 sm:flex-row sm:items-center">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-[560]">
                    {[r.row.first_name, r.row.last_name].filter(Boolean).join(" ") || "(no name)"}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {[r.row.email, r.row.organization].filter(Boolean).join(" · ")}
                    {r.match_name && ` → matches ${r.match_name}`}
                    {r.conflicts?.length > 0 && ` · conflicts: ${r.conflicts.join(", ")}`}
                    {r.errors?.length > 0 && ` · issues: ${r.errors.join(", ")}`}
                  </span>
                </span>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-[600] ${STATE_STYLE[r.state]}`}>
                  {r.state.replace("_", " ")}
                </span>
                <select value={decisions[r.index] || "skip"} data-testid={`import-decision-${r.index}`}
                  aria-label={`Decision for row ${r.index + 1}`}
                  onChange={(e) => setDecisions((d) => ({ ...d, [r.index]: e.target.value }))}
                  className="h-9 shrink-0 rounded-md border border-input bg-background px-2 text-sm">
                  {STATES.filter((s) => s === "skip" || s === r.state
                    || (s === "new" && r.state !== "skip")
                    || (s === "existing" && r.match_contact_id)).map((s) => (
                      <option key={s} value={s}>{s.replace("_", " ")}</option>
                    ))}
                </select>
              </li>
            ))}
          </ul>
        </section>
      )}

      {history.length > 0 && (
        <section className="mt-8" data-testid="import-history">
          <h2 className="text-base font-[640]">Recent imports</h2>
          <ul className="mt-2 space-y-1.5">
            {history.map((b) => (
              <li key={b.id} data-testid={`import-history-${b.id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {b.kind.toUpperCase()} · {b.state} · {b.created_contact_ids?.length || 0} created
                  · {new Date(b.created_at).toLocaleDateString()}
                </span>
                {b.state === "committed" && (b.created_contact_ids?.length || 0) > 0 && (
                  <Button variant="outline" size="sm" onClick={() => undo(b.id)}
                    data-testid={`import-undo-${b.id}`}>
                    <Undo2 className="mr-1.5 h-4 w-4" /> Undo
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
